import { CONNECTION_STATE_CHANGE, PubSub } from '@aws-amplify/pubsub';
import { fetchAuthSession } from 'aws-amplify/auth';
import awsExports from '../aws-exports';
import { Hub } from 'aws-amplify/utils';
import { attachIdentityIdToPolicyViaAPI } from '../network/network_callbacks';
import { authService } from './authService';
import { logger } from '../utils/logger';

// Supported events
const SUBSCRIBE_EVENTS = [
    'start_game',
    'end_game',
    'pause_game',
    'game-hold',
    'game-resume',
    'show_entity',
    'user-claim',
    'user-join',
    'captain-claim',
    'claim-result',
    'captain-claim-result',
    'swap_request',
    'swap_failure',
    'swap_success',
    'USER_JOINED',
    'Game_Abandon',
    'countdown',
    'next_question',
    'submit_answer',
    'results',
    'game_end',
];

const PUBLISH_EVENTS = [
    'user-claim',
    'game-hold',
    'game-resume',
    'submit_answer',
];

class MQTTService {

    constructor() {
        this.topic = null;
        this.pubSub = null;
        this.subscription = null;
        this.eventHandlers = {};
        this.subOptions = null;
        this.lastMessageHash = null;
    }

    /**
     * 
     * @param {*} topic - 'tambola/game/' & 'tambola/room/' (for example)
     * @param {*} gameId - unique string game identifier
     * @param {*} eventHandlers - object with event names as keys and handler functions as values
     */
    async connect(topic, gameId, eventHandlers = {}) {
        logger.log('Connecting to MQTT for gameId:', gameId);
        this.topic = `${topic}${gameId}`;
        this.eventHandlers = eventHandlers;
        const endpoint = awsExports?.aws_iot_endpoint;
        const region = awsExports?.aws_project_region || awsExports?.aws_cognito_region;
        if (!endpoint) throw new Error('AWS IoT endpoint missing in aws-exports.js');

        // Drop prior subscription so reconnect does not stack listeners
        if (this.subscription) {
            try { this.subscription.unsubscribe(); } catch (e) { console.error('Error during MQTT unsubscribe:', e); }
            this.subscription = null;
        }

        const session = await fetchAuthSession();
        const credentials = session?.credentials;

        // const userAuthTokenDetails = await authService.getAuthTokens();
        // logger.log('User userAuthTokenDetails after sign up:', userAuthTokenDetails);
        // const IotPolicyResult = await attachIdentityIdToPolicyViaAPI(userAuthTokenDetails?.identityId);
        // logger.log('User IoT policy attachment result:', IotPolicyResult);

        if (!this.pubSub) {
            this.pubSub = new PubSub({ region, endpoint });

            Hub.listen(this.topic, (data) => {
                const { payload } = data;
                if (payload.event === CONNECTION_STATE_CHANGE) {
                    const connectionState = payload.data.connectionState;
                    logger.log("PubSub connection state:", connectionState);
                }
                else {
                    logger.log('PubSub event:', payload);
                }
            });
            try {
                if (credentials) {
                    this.pubSub.configure({ region, endpoint, credentials });
                } else {
                    this.pubSub.configure({ region, endpoint });
                }
            } catch (e) {
                console.error('Error fetching auth session for MQTT:', e);
                this.pubSub.configure({ region, endpoint });
            }
        }
        const clientId = `web-${Math.random().toString(36).slice(2)}`;
        this.subOptions = { clientId };
        try {
            this.subOptions = { ...this.subOptions, credentials: credentials };
        } catch (e) {
            console.error('Error fetching auth session for MQTT subscription:', e);
        }
        const observable = this.pubSub.subscribe({ topics: [this.topic], options: this.subOptions });
        this.subscription = observable.subscribe({
            next: (message) => {
                // Deduplication logic: only handle if message is different from last
                logger.log('MQTT raw message received:', message);
                const messageHash = JSON.stringify(message);
                if (this.lastMessageHash === messageHash) {
                    // Duplicate message, ignore
                    return;
                }
                this.lastMessageHash = messageHash;
                const { event, timestamp, index, entity } = message || {};
                const { time, entityId } = entity || {};
                const entityData = entity?.entity || {};
                if (SUBSCRIBE_EVENTS.includes(event) && typeof this.eventHandlers[event] === 'function') {
                    this.eventHandlers[event](message);
                }
            },
            error: (err) => {
                console.error('MQTT subscription error:', err);
                if (typeof this.eventHandlers['error'] === 'function') {
                    this.eventHandlers['error'](err);
                }
            },
            complete: () => {
                logger.log('MQTT subscription completed');
                if (typeof this.eventHandlers['complete'] === 'function') {
                    this.eventHandlers['complete']();
                }
            },
        });
    }

    async publish(event, payload) {
        if (!this.pubSub || !this.topic) throw new Error('MQTT not connected');
        if (!PUBLISH_EVENTS.includes(event)) throw new Error(`Event ${event} not allowed for publish`);
        const message = {
            event: event,
            ...payload
        };
        logger.log(this.topic);
        //logger.log(this.subOptions);
        logger.log(JSON.stringify(message));
        try {
            Hub.dispatch(
                this.topic,
                {
                    event: event,
                    data: payload,
                    //message: payload
                });
            const result = await this.pubSub.publish(
                { topics: this.topic, messages: JSON.stringify(message) },
                //options: { ...this.subOptions } //provider : AWSIoTProvider
            );
            logger.log('MQTT publish success:', result);
            return result;
        } catch (error) {
            console.error('MQTT publish error:', error);
            throw error;
        }
    }

    disconnect() {
        logger.log('Disconnecting MQTT for Topic : ', this.topic);
        if (this.subscription) {
            try { this.subscription.unsubscribe(); } catch (e) { console.error('Error during MQTT unsubscribe:', e); }
            this.subscription = null;
        }
        this.pubSub = null;
        this.topic = null;
        this.eventHandlers = {};
        this.subOptions = null;
    }
}

const mqttService = new MQTTService();
export default mqttService;
